import {
  ACCOUNTS,
  TRANSACTIONS,
  BUDGET_CATEGORIES,
  INVESTMENTS,
  RECURRING_TRANSACTIONS,
  MONTHLY_SPENDING_TREND,
  PROJECTED_MONTHS,
  fmt,
} from "../../data/mockData";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function downloadCSV(filename, csvString) {
  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function openHTML(htmlString) {
  const blob = new Blob([htmlString], { type: "text/html;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  window.open(url, "_blank");
}

const TODAY = "March 14, 2026";

// Shared HTML boilerplate
function htmlWrap(title, body) {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${title} — Ledger Report</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=DM+Sans:wght@300;400;500&family=DM+Mono:wght@300;400;500&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'DM Sans', sans-serif; background: #fff; color: #121A12; padding: 40px; max-width: 960px; margin: 0 auto; }
    h1  { font-family: 'Capsule Sans', sans-serif; font-size: 28px; font-weight: 700; color: #121A12; margin-bottom: 4px; }
    h2  { font-family: 'Capsule Sans', sans-serif; font-size: 20px; font-weight: 600; color: #121A12; margin: 32px 0 12px; }
    .meta { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #777; margin-bottom: 32px; border-bottom: 2px solid #121A12; padding-bottom: 16px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
    th { font-family: 'JetBrains Mono', monospace; font-size: 10px; text-transform: uppercase; letter-spacing: 0.1em; color: #777; text-align: left; padding: 6px 12px; border-bottom: 1px solid #e0ddd8; }
    td { font-size: 13px; color: #333; padding: 10px 12px; border-bottom: 1px solid #f0ede8; vertical-align: top; }
    tr:last-child td { border-bottom: none; }
    .pos { color: #2a8a4e; font-family: 'JetBrains Mono', monospace; }
    .neg { color: #b94040; font-family: 'JetBrains Mono', monospace; }
    .num { font-family: 'JetBrains Mono', monospace; }
    .footer { margin-top: 48px; padding-top: 16px; border-top: 1px solid #e0ddd8; font-family: 'JetBrains Mono', monospace; font-size: 10px; color: #aaa; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  <h1>Ledger</h1>
  <div class="meta">Generated on ${TODAY} · Premium Plan</div>
  ${body}
  <div class="footer">Ledger Premium · ${TODAY} · Data is illustrative mock data</div>
</body>
</html>`;
}

// ─── Accounts ─────────────────────────────────────────────────────────────────

function accountsCSV() {
  const rows = ["Account Name,Type,Bank,Balance,Monthly Change"];
  ACCOUNTS.forEach(a => rows.push(`"${a.name}","${a.type}","${a.bank}",${a.balance},${a.change}`));
  return rows.join("\n");
}

function accountsHTML() {
  const rows = ACCOUNTS.map(a => {
    const cls = a.balance >= 0 ? "pos" : "neg";
    const chgCls = a.change >= 0 ? "pos" : "neg";
    return `<tr>
      <td>${a.icon} ${a.name}</td>
      <td style="text-transform:capitalize">${a.type}</td>
      <td>${a.bank}</td>
      <td class="${cls} num">${fmt(a.balance)}</td>
      <td class="${chgCls} num">${a.change >= 0 ? "+" : ""}${fmt(a.change)}</td>
    </tr>`;
  }).join("");
  const totalAssets = ACCOUNTS.filter(a => a.balance > 0).reduce((s, a) => s + a.balance, 0);
  const totalDebt   = ACCOUNTS.filter(a => a.balance < 0).reduce((s, a) => s + a.balance, 0);
  const netWorth    = totalAssets + totalDebt;
  return htmlWrap("Accounts", `
    <h2>Accounts Summary</h2>
    <table>
      <thead><tr><th>Account</th><th>Type</th><th>Institution</th><th>Balance</th><th>Monthly Change</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <table>
      <thead><tr><th>Summary</th><th>Amount</th></tr></thead>
      <tbody>
        <tr><td>Total Assets</td><td class="pos num">${fmt(totalAssets)}</td></tr>
        <tr><td>Total Debt</td><td class="neg num">${fmt(totalDebt)}</td></tr>
        <tr><td><strong>Net Worth</strong></td><td class="pos num"><strong>${fmt(netWorth)}</strong></td></tr>
      </tbody>
    </table>
  `);
}

// ─── Transactions ──────────────────────────────────────────────────────────────

function transactionsCSV() {
  const rows = ["Date,Merchant,Category,Account,Amount"];
  TRANSACTIONS.forEach(t => rows.push(`"${t.date}","${t.merchant}","${t.category}","${t.account}",${t.amount}`));
  return rows.join("\n");
}

function transactionsHTML() {
  const rows = TRANSACTIONS.map(t => {
    const cls = t.amount >= 0 ? "pos" : "neg";
    return `<tr>
      <td class="num">${t.date}</td>
      <td>${t.icon} ${t.merchant}</td>
      <td>${t.category}</td>
      <td>${t.account}</td>
      <td class="${cls} num">${t.amount >= 0 ? "+" : ""}${fmt(t.amount)}</td>
    </tr>`;
  }).join("");
  return htmlWrap("Transactions", `
    <h2>Recent Transactions</h2>
    <table>
      <thead><tr><th>Date</th><th>Merchant</th><th>Category</th><th>Account</th><th>Amount</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
  `);
}

// ─── Budget ────────────────────────────────────────────────────────────────────

function getProjected(catName) {
  const KEY_MAP = { Housing: "housing", Groceries: "groceries", Transport: "transport", Dining: "dining", Subscriptions: "subscriptions", Shopping: "shopping" };
  const key = KEY_MAP[catName];
  if (!key) return 0;
  if (catName === "Housing") return 1850;
  const last3 = MONTHLY_SPENDING_TREND.slice(-3);
  const avg = last3.reduce((s, m) => s + (m[key] || 0), 0) / 3;
  return Math.round(avg);
}

function budgetCSV() {
  const rows = ["Category,Allocated,Spent,Projected EOM,Remaining,% Used,Status"];
  BUDGET_CATEGORIES.forEach(c => {
    const proj = getProjected(c.name);
    const remaining = c.allocated - c.spent;
    const pct = Math.round((c.spent / c.allocated) * 100);
    const status = c.spent > c.allocated ? "Over Budget" : proj > c.allocated ? "Watch" : "On Track";
    rows.push(`"${c.name}",${c.allocated},${c.spent},${proj},${remaining},${pct}%,"${status}"`);
  });
  return rows.join("\n");
}

function budgetHTML() {
  const rows = BUDGET_CATEGORIES.map(c => {
    const proj = getProjected(c.name);
    const remaining = c.allocated - c.spent;
    const pct = Math.round((c.spent / c.allocated) * 100);
    const over = c.spent > c.allocated;
    const projOver = proj > c.allocated;
    const status = over ? `<span style="color:#b94040">⚠ Over Budget</span>` : projOver ? `<span style="color:#c8963a">◎ Watch</span>` : `<span style="color:#2a8a4e">✓ On Track</span>`;
    return `<tr>
      <td>${c.name}</td>
      <td class="num">${fmt(c.allocated)}</td>
      <td class="${over ? "neg" : ""} num">${fmt(c.spent)}</td>
      <td class="num">${fmt(proj)}</td>
      <td class="${remaining < 0 ? "neg" : "pos"} num">${fmt(remaining)}</td>
      <td class="num">${pct}%</td>
      <td>${status}</td>
    </tr>`;
  }).join("");
  const totalAllocated = BUDGET_CATEGORIES.reduce((s, c) => s + c.allocated, 0);
  const totalSpent     = BUDGET_CATEGORIES.reduce((s, c) => s + c.spent, 0);
  const totalProj      = BUDGET_CATEGORIES.reduce((s, c) => s + getProjected(c.name), 0);
  return htmlWrap("Budget", `
    <h2>Budget Overview — March 2026</h2>
    <table>
      <thead><tr><th>Category</th><th>Allocated</th><th>Spent</th><th>Projected EOM</th><th>Remaining</th><th>% Used</th><th>Status</th></tr></thead>
      <tbody>
        ${rows}
        <tr style="background:#f8f6f2">
          <td><strong>Total</strong></td>
          <td class="num"><strong>${fmt(totalAllocated)}</strong></td>
          <td class="num"><strong>${fmt(totalSpent)}</strong></td>
          <td class="num"><strong>${fmt(totalProj)}</strong></td>
          <td class="num"><strong>${fmt(totalAllocated - totalSpent)}</strong></td>
          <td class="num"><strong>${Math.round((totalSpent / totalAllocated) * 100)}%</strong></td>
          <td></td>
        </tr>
      </tbody>
    </table>
    <h2>Monthly Spending History</h2>
    <table>
      <thead><tr><th>Month</th><th>Total</th><th>Groceries</th><th>Dining</th><th>Transport</th><th>Subscriptions</th><th>Shopping</th><th>Utilities</th></tr></thead>
      <tbody>
        ${MONTHLY_SPENDING_TREND.map(m => `<tr>
          <td>${m.month}</td>
          <td class="num">${fmt(m.total)}</td>
          <td class="num">${fmt(m.groceries)}</td>
          <td class="num">${fmt(m.dining)}</td>
          <td class="num">${fmt(m.transport)}</td>
          <td class="num">${fmt(m.subscriptions)}</td>
          <td class="num">${fmt(m.shopping)}</td>
          <td class="num">${fmt(m.utilities)}</td>
        </tr>`).join("")}
      </tbody>
    </table>
  `);
}

// ─── Investments ───────────────────────────────────────────────────────────────

function getManualInvestments() {
  try {
    return JSON.parse(localStorage.getItem("ledger_manual_investments") || "[]");
  } catch { return []; }
}

function investmentsCSV() {
  const manual = getManualInvestments();
  const rows = ["Ticker/Name,Full Name,Shares,Current Price,Avg Price,Market Value,Gain %,Gain $"];
  INVESTMENTS.forEach(inv =>
    rows.push(`"${inv.ticker}","${inv.name}",${inv.shares},${inv.price},${inv.avgPrice},${inv.value},${inv.gain}%,${inv.gainAmt}`)
  );
  manual.forEach(inv =>
    rows.push(`"${inv.name || inv.ticker || "—"}","${inv.fullName || inv.description || "Manual Entry"}","—","—","—","${inv.currentValue || inv.value || 0}","—","—"`)
  );
  return rows.join("\n");
}

function investmentsHTML() {
  const manual = getManualInvestments();
  const rows = INVESTMENTS.map(inv => `<tr>
    <td><strong>${inv.ticker}</strong></td>
    <td>${inv.name}</td>
    <td class="num">${inv.shares}</td>
    <td class="num">${fmt(inv.price)}</td>
    <td class="num">${fmt(inv.avgPrice)}</td>
    <td class="num">${fmt(inv.value)}</td>
    <td class="${inv.gain >= 0 ? "pos" : "neg"} num">${inv.gain >= 0 ? "+" : ""}${inv.gain}%</td>
    <td class="${inv.gainAmt >= 0 ? "pos" : "neg"} num">${inv.gainAmt >= 0 ? "+" : ""}${fmt(inv.gainAmt)}</td>
  </tr>`).join("");
  const totalValue   = INVESTMENTS.reduce((s, i) => s + i.value, 0);
  const totalGainAmt = INVESTMENTS.reduce((s, i) => s + i.gainAmt, 0);

  const manualSection = manual.length > 0 ? `
    <h2>Manual Investments</h2>
    <table>
      <thead><tr><th>Name</th><th>Type</th><th>Current Value</th><th>Notes</th></tr></thead>
      <tbody>
        ${manual.map(inv => `<tr>
          <td>${inv.name || "—"}</td>
          <td style="text-transform:capitalize">${inv.type || "—"}</td>
          <td class="num">${fmt(parseFloat(inv.currentValue || inv.value || 0))}</td>
          <td style="color:#888;font-size:12px">${inv.description || inv.address || ""}</td>
        </tr>`).join("")}
      </tbody>
    </table>
  ` : "";

  return htmlWrap("Investments", `
    <h2>Portfolio Holdings</h2>
    <table>
      <thead><tr><th>Ticker</th><th>Name</th><th>Shares</th><th>Price</th><th>Avg Cost</th><th>Market Value</th><th>Gain %</th><th>Gain $</th></tr></thead>
      <tbody>
        ${rows}
        <tr style="background:#f8f6f2">
          <td colspan="5"><strong>Total</strong></td>
          <td class="num"><strong>${fmt(totalValue)}</strong></td>
          <td></td>
          <td class="pos num"><strong>+${fmt(totalGainAmt)}</strong></td>
        </tr>
      </tbody>
    </table>
    ${manualSection}
  `);
}

// ─── Recurring ─────────────────────────────────────────────────────────────────

function recurringCSV() {
  const rows = ["Merchant,Category,Amount,Frequency,Monthly Equiv,Next Date,Annual Cost,Status"];
  RECURRING_TRANSACTIONS.forEach(r => {
    const FREQ = { Monthly: 1, "Bi-Weekly": 2.17, Weekly: 4.33, Quarterly: 0.33, Annually: 0.083 };
    const monthly = r.amount * (FREQ[r.frequency] || 1);
    const annual  = monthly * 12;
    rows.push(`"${r.merchant}","${r.category}",${r.amount},"${r.frequency}",${monthly.toFixed(2)},"${r.nextDate}",${annual.toFixed(2)},"${r.active ? "Active" : "Inactive"}"`);
  });
  return rows.join("\n");
}

function recurringHTML() {
  const FREQ = { Monthly: 1, "Bi-Weekly": 2.17, Weekly: 4.33, Quarterly: 0.33, Annually: 0.083 };
  const active   = RECURRING_TRANSACTIONS.filter(r => r.active);
  const inactive = RECURRING_TRANSACTIONS.filter(r => !r.active);
  const totalMonthly = active.reduce((s, r) => s + r.amount * (FREQ[r.frequency] || 1), 0);
  const totalAnnual  = totalMonthly * 12;

  const buildRows = items => items.map(r => {
    const monthly = r.amount * (FREQ[r.frequency] || 1);
    const annual  = monthly * 12;
    return `<tr>
      <td>${r.icon} ${r.merchant}</td>
      <td>${r.category}</td>
      <td class="num">${fmt(r.amount)}</td>
      <td>${r.frequency}</td>
      <td class="num">${fmt(monthly)}/mo</td>
      <td>${r.nextDate}</td>
      <td class="num">${fmt(annual)}</td>
      <td style="color:${r.active ? "#2a8a4e" : "#aaa"}">${r.active ? "● Active" : "○ Inactive"}</td>
    </tr>`;
  }).join("");

  return htmlWrap("Recurring Transactions", `
    <h2>Recurring Transactions</h2>
    <p style="font-family:'DM Mono',monospace;font-size:12px;color:#777;margin-bottom:20px">
      Active monthly total: <strong style="color:#333">${fmt(totalMonthly)}/mo</strong> · Annual: <strong style="color:#333">${fmt(totalAnnual)}</strong>
    </p>
    <table>
      <thead><tr><th>Merchant</th><th>Category</th><th>Amount</th><th>Frequency</th><th>Monthly Equiv</th><th>Next Date</th><th>Annual Cost</th><th>Status</th></tr></thead>
      <tbody>
        ${buildRows(active)}
        ${inactive.length > 0 ? `<tr><td colspan="8" style="padding:16px 12px 6px;font-family:'DM Mono',monospace;font-size:10px;text-transform:uppercase;letter-spacing:0.1em;color:#aaa">Inactive</td></tr>${buildRows(inactive)}` : ""}
      </tbody>
    </table>
  `);
}

// ─── Insights (Spending Trends) ───────────────────────────────────────────────

function insightsCSV() {
  const rows = ["Month,Total,Groceries,Dining,Transport,Subscriptions,Shopping,Utilities,Health,Other,Type"];
  MONTHLY_SPENDING_TREND.forEach(m =>
    rows.push(`"${m.month}",${m.total},${m.groceries},${m.dining},${m.transport},${m.subscriptions},${m.shopping},${m.utilities},${m.health},${m.other},"Actual"`)
  );
  PROJECTED_MONTHS.forEach(p =>
    rows.push(`"${p.month}",${p.base},${p.breakdown.groceries},${p.breakdown.dining},${p.breakdown.transport},${p.breakdown.subscriptions},${p.breakdown.shopping},${p.breakdown.utilities},${p.breakdown.health},${p.breakdown.other},"Projected"`)
  );
  return rows.join("\n");
}

function insightsHTML() {
  const histRows = MONTHLY_SPENDING_TREND.map(m => `<tr>
    <td>${m.month}</td>
    <td class="num">${fmt(m.total)}</td>
    <td class="num">${fmt(m.groceries)}</td>
    <td class="num">${fmt(m.dining)}</td>
    <td class="num">${fmt(m.transport)}</td>
    <td class="num">${fmt(m.subscriptions)}</td>
    <td class="num">${fmt(m.shopping)}</td>
    <td class="num">${fmt(m.utilities)}</td>
    <td style="font-family:'DM Mono',monospace;font-size:11px;color:#aaa">Actual</td>
  </tr>`).join("");
  const projRows = PROJECTED_MONTHS.map(p => `<tr style="background:#fffbf5">
    <td>${p.month}</td>
    <td class="num" style="color:#c8963a">${fmt(p.base)} <span style="font-size:10px;color:#aaa">(${fmt(p.low)}–${fmt(p.high)})</span></td>
    <td class="num">${fmt(p.breakdown.groceries)}</td>
    <td class="num">${fmt(p.breakdown.dining)}</td>
    <td class="num">${fmt(p.breakdown.transport)}</td>
    <td class="num">${fmt(p.breakdown.subscriptions)}</td>
    <td class="num">${fmt(p.breakdown.shopping)}</td>
    <td class="num">${fmt(p.breakdown.utilities)}</td>
    <td style="font-family:'DM Mono',monospace;font-size:11px;color:#c8963a">Projected</td>
  </tr>`).join("");
  const avg = Math.round(MONTHLY_SPENDING_TREND.reduce((s, m) => s + m.total, 0) / MONTHLY_SPENDING_TREND.length);
  return htmlWrap("Spending Insights", `
    <h2>Spending Trends — Mar 2025 to May 2026</h2>
    <p style="font-family:'DM Mono',monospace;font-size:12px;color:#777;margin-bottom:20px">12-month average: <strong style="color:#333">${fmt(avg)}/mo</strong></p>
    <table>
      <thead><tr><th>Month</th><th>Total</th><th>Groceries</th><th>Dining</th><th>Transport</th><th>Subscriptions</th><th>Shopping</th><th>Utilities</th><th>Type</th></tr></thead>
      <tbody>${histRows}${projRows}</tbody>
    </table>
  `);
}

// ─── Full Report ───────────────────────────────────────────────────────────────

function fullReportCSV() {
  return [
    "=== ACCOUNTS ===",
    accountsCSV(),
    "",
    "=== TRANSACTIONS ===",
    transactionsCSV(),
    "",
    "=== BUDGET ===",
    budgetCSV(),
    "",
    "=== INVESTMENTS ===",
    investmentsCSV(),
    "",
    "=== RECURRING ===",
    recurringCSV(),
    "",
    "=== SPENDING TRENDS ===",
    insightsCSV(),
  ].join("\n");
}

function fullReportHTML() {
  const totalAssets  = ACCOUNTS.filter(a => a.balance > 0).reduce((s, a) => s + a.balance, 0);
  const totalDebt    = ACCOUNTS.filter(a => a.balance < 0).reduce((s, a) => s + a.balance, 0);
  const netWorth     = totalAssets + totalDebt;
  const totalBudget  = BUDGET_CATEGORIES.reduce((s, c) => s + c.allocated, 0);
  const totalSpent   = BUDGET_CATEGORIES.reduce((s, c) => s + c.spent, 0);
  const totalInvest  = INVESTMENTS.reduce((s, i) => s + i.value, 0);
  const FREQ         = { Monthly: 1, "Bi-Weekly": 2.17, Weekly: 4.33, Quarterly: 0.33, Annually: 0.083 };
  const totalRecur   = RECURRING_TRANSACTIONS.filter(r => r.active).reduce((s, r) => s + r.amount * (FREQ[r.frequency] || 1), 0);

  const summaryCards = [
    { label: "Net Worth",         val: fmt(netWorth),          sub: `Assets: ${fmt(totalAssets)} · Debt: ${fmt(totalDebt)}`,    col: "#2a8a4e" },
    { label: "Budget Used",       val: `${Math.round((totalSpent/totalBudget)*100)}%`, sub: `${fmt(totalSpent)} of ${fmt(totalBudget)}`, col: "#c8963a" },
    { label: "Portfolio Value",   val: fmt(totalInvest),       sub: "Across all holdings",                                      col: "#4a7abf" },
    { label: "Monthly Recurring", val: fmt(totalRecur),        sub: `${fmt(totalRecur * 12)} annually`,                         col: "#b94040" },
  ].map(c => `<div style="flex:1;min-width:180px;border:1px solid #e0ddd8;border-radius:8px;padding:16px">
    <div style="font-family:'DM Mono',monospace;font-size:10px;color:#999;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:6px">${c.label}</div>
    <div style="font-family:'Cormorant Garamond',serif;font-size:24px;font-weight:600;color:${c.col}">${c.val}</div>
    <div style="font-size:11px;color:#999;margin-top:4px">${c.sub}</div>
  </div>`).join("");

  return htmlWrap("Full Financial Report", `
    <h2>Financial Summary</h2>
    <div style="display:flex;gap:16px;flex-wrap:wrap;margin-bottom:32px">${summaryCards}</div>

    <h2>Accounts</h2>
    ${accountsHTML().match(/<table[\s\S]*?<\/table>/)?.[0] || ""}

    <h2>Recent Transactions</h2>
    ${transactionsHTML().match(/<table[\s\S]*?<\/table>/)?.[0] || ""}

    <h2>Budget — March 2026</h2>
    ${budgetHTML().match(/<table[\s\S]*?<\/table>/)?.[0] || ""}

    <h2>Portfolio Holdings</h2>
    ${investmentsHTML().match(/<table[\s\S]*?<\/table>/)?.[0] || ""}

    <h2>Recurring Payments</h2>
    ${recurringHTML().match(/<table[\s\S]*?<\/table>/)?.[0] || ""}

    <h2>Spending Trend (12 months)</h2>
    ${insightsHTML().match(/<table[\s\S]*?<\/table>/)?.[0] || ""}
  `);
}

// ─── Report Cards config ───────────────────────────────────────────────────────

const REPORTS = [
  {
    id: "accounts",
    label: "Accounts",
    desc: "All linked accounts, balances, and monthly changes",
    icon: "◉",
    color: "#7A9BAF",
    onCSV:  () => downloadCSV("ledger_accounts.csv", accountsCSV()),
    onHTML: () => openHTML(accountsHTML()),
  },
  {
    id: "transactions",
    label: "Transactions",
    desc: "Recent transactions across all accounts",
    icon: "⊞",
    color: "#52D68A",
    onCSV:  () => downloadCSV("ledger_transactions.csv", transactionsCSV()),
    onHTML: () => openHTML(transactionsHTML()),
  },
  {
    id: "budget",
    label: "Budget",
    desc: "Monthly budget, spending, projections, and history",
    icon: "◎",
    color: "#F4B8B8",
    onCSV:  () => downloadCSV("ledger_budget.csv", budgetCSV()),
    onHTML: () => openHTML(budgetHTML()),
  },
  {
    id: "investments",
    label: "Investments",
    desc: "Portfolio holdings, gains, and manual investments",
    icon: "△",
    color: "#D4AF37",
    onCSV:  () => downloadCSV("ledger_investments.csv", investmentsCSV()),
    onHTML: () => openHTML(investmentsHTML()),
  },
  {
    id: "recurring",
    label: "Recurring",
    desc: "Subscriptions and recurring bills with annual cost",
    icon: "↻",
    color: "#D4B8E8",
    onCSV:  () => downloadCSV("ledger_recurring.csv", recurringCSV()),
    onHTML: () => openHTML(recurringHTML()),
  },
  {
    id: "insights",
    label: "Spending Insights",
    desc: "12-month spending trends and 3-month projections",
    icon: "◈",
    color: "#F4D8B8",
    onCSV:  () => downloadCSV("ledger_insights.csv", insightsCSV()),
    onHTML: () => openHTML(insightsHTML()),
  },
];

// ─── Component ─────────────────────────────────────────────────────────────────

export default function ReportsModal({ onClose }) {
  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 300,
        background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "24px",
      }}
    >
      <div style={{
        background: "var(--bg-card)", border: "1px solid var(--border-strong)",
        borderRadius: "16px", width: "100%", maxWidth: "760px",
        maxHeight: "88vh", overflow: "hidden",
        display: "flex", flexDirection: "column",
        boxShadow: "0 32px 80px rgba(0,0,0,0.7)",
        animation: "modalIn 0.22s cubic-bezier(0.4,0,0.2,1)",
      }}>
        <style>{`
          @keyframes modalIn {
            from { opacity: 0; transform: scale(0.96) translateY(8px); }
            to   { opacity: 1; transform: scale(1) translateY(0); }
          }
          .rep-btn:hover { border-color: #D4AF37 !important; color: #D4AF37 !important; }
          .rep-card:hover { background: var(--bg-hover) !important; border-color: var(--border-active) !important; }
        `}</style>

        {/* Header */}
        <div style={{
          padding: "24px 28px 20px", borderBottom: "1px solid var(--border)",
          display: "flex", justifyContent: "space-between", alignItems: "flex-start",
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: "20px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "600", color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
              Export Reports
            </div>
            <div style={{ fontSize: "11px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace", marginTop: "4px" }}>
              Download data as CSV or open as print-ready HTML
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: "32px", height: "32px", borderRadius: "8px", border: "1px solid var(--border-strong)",
              background: "transparent", color: "var(--text-faint)", cursor: "pointer", fontSize: "16px",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              transition: "color 0.15s, border-color 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.color = "var(--text-primary)"; e.currentTarget.style.borderColor = "var(--border-strong)"; }}
            onMouseLeave={e => { e.currentTarget.style.color = "var(--text-faint)"; e.currentTarget.style.borderColor = "var(--border)"; }}
          >×</button>
        </div>

        {/* Scrollable body */}
        <div style={{ padding: "20px 28px 24px", overflowY: "auto", flex: 1 }}>

          {/* Full Report — featured card */}
          <div style={{
            background: "linear-gradient(135deg, rgba(212,175,55,0.12), rgba(212,175,55,0.04))",
            border: "1px solid rgba(212,175,55,0.25)", borderRadius: "12px",
            padding: "20px 24px", marginBottom: "20px",
            display: "flex", justifyContent: "space-between", alignItems: "center", gap: "16px",
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "13px", color: "#D4AF37", fontWeight: "600", marginBottom: "4px" }}>
                ◈ Full Financial Report
              </div>
              <div style={{ fontSize: "12px", color: "var(--text-faint)" }}>
                Complete snapshot — accounts, transactions, budget, investments, recurring, and spending trends in one document
              </div>
            </div>
            <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
              <button
                className="rep-btn"
                onClick={() => downloadCSV("ledger_full_report.csv", fullReportCSV())}
                style={{
                  padding: "8px 16px", borderRadius: "8px",
                  background: "transparent", border: "1px solid var(--border-active)",
                  color: "var(--text-muted)", fontSize: "11px", fontFamily: "'JetBrains Mono', monospace",
                  cursor: "pointer", transition: "border-color 0.15s, color 0.15s",
                }}
              >↓ CSV</button>
              <button
                className="rep-btn"
                onClick={() => openHTML(fullReportHTML())}
                style={{
                  padding: "8px 16px", borderRadius: "8px",
                  background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.3)",
                  color: "#D4AF37", fontSize: "11px", fontFamily: "'JetBrains Mono', monospace",
                  cursor: "pointer", transition: "border-color 0.15s, background 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(212,175,55,0.18)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(212,175,55,0.1)"; }}
              >⎘ HTML / PDF</button>
            </div>
          </div>

          {/* Section label */}
          <div style={{ fontSize: "10px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "12px" }}>
            Individual Reports
          </div>

          {/* Report cards grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
            {REPORTS.map(r => (
              <div
                key={r.id}
                className="rep-card"
                style={{
                  background: "var(--bg-surface)", border: "1px solid var(--border)",
                  borderRadius: "10px", padding: "16px",
                  transition: "background 0.15s, border-color 0.15s",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                  <span style={{ fontSize: "14px", color: r.color }}>{r.icon}</span>
                  <span style={{ fontSize: "13px", color: "var(--text-secondary)", fontWeight: "500" }}>{r.label}</span>
                </div>
                <div style={{ fontSize: "11px", color: "var(--text-faint)", marginBottom: "14px", lineHeight: 1.5 }}>
                  {r.desc}
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    className="rep-btn"
                    onClick={r.onCSV}
                    style={{
                      flex: 1, padding: "7px 0", borderRadius: "7px",
                      background: "transparent", border: "1px solid var(--border-strong)",
                      color: "var(--text-faint)", fontSize: "11px", fontFamily: "'JetBrains Mono', monospace",
                      cursor: "pointer", transition: "border-color 0.15s, color 0.15s",
                    }}
                  >↓ CSV</button>
                  <button
                    className="rep-btn"
                    onClick={r.onHTML}
                    style={{
                      flex: 1, padding: "7px 0", borderRadius: "7px",
                      background: "transparent", border: "1px solid var(--border-strong)",
                      color: "var(--text-faint)", fontSize: "11px", fontFamily: "'JetBrains Mono', monospace",
                      cursor: "pointer", transition: "border-color 0.15s, color 0.15s",
                    }}
                  >⎘ HTML</button>
                </div>
              </div>
            ))}
          </div>

          {/* Print tip */}
          <div style={{
            marginTop: "20px", padding: "12px 16px",
            background: "var(--bg-main)", border: "1px solid var(--border)", borderRadius: "8px",
            display: "flex", alignItems: "center", gap: "10px",
          }}>
            <span style={{ fontSize: "14px" }}>💡</span>
            <span style={{ fontSize: "11px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace" }}>
              HTML reports open in a new tab. Use <strong style={{ color: "var(--text-faint)" }}>File → Print → Save as PDF</strong> to export as PDF.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
